import React, { useState, useEffect } from "react";
import { ForecastAccuracyItem, ForecastAccuracyTabProps } from "./forecast-accuracy/types";
import { FilterHeader } from "./forecast-accuracy/FilterHeader";
import { HistoricalTrendsChart } from "./forecast-accuracy/HistoricalTrendsChart";
import { ForecastAccuracyChart } from "./forecast-accuracy/ForecastAccuracyChart";
import { ImprovementsPanel } from "./forecast-accuracy/ImprovementsPanel";
import { DataTable } from "./forecast-accuracy/DataTable";
import { useLeaderboardData } from "./forecast-accuracy/hooks/useLeaderboardData";
import { useFilterOptions } from "./forecast-accuracy/hooks/useFilterOptions";
import { useChartData } from "./forecast-accuracy/hooks/useChartData";
import { calculateAccuracy } from "./forecast-accuracy/utils";

const ForecastAccuracyTab: React.FC<ForecastAccuracyTabProps> = ({ className }) => {
  const { leaderboardData, filteredData, setFilteredData } = useLeaderboardData();
  
  const [isTableOpen, setIsTableOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string>("All");
  const [selectedCountry, setSelectedCountry] = useState<string>("All");
  const [selectedAccountName, setSelectedAccountName] = useState<string>("All");
  const [selectedProductLine, setSelectedProductLine] = useState<string>("All");
  const [selectedIndustry, setSelectedIndustry] = useState<string>("All");
  const [filterType, setFilterType] = useState<"product" | "industry">("product");
  const [overForecastedItems, setOverForecastedItems] = useState<ForecastAccuracyItem[]>([]);
  const [underForecastedItems, setUnderForecastedItems] = useState<ForecastAccuracyItem[]>([]);

  const { countryOptions, accountOptions } = useFilterOptions(selectedRegion, selectedCountry);
  const { chartData, historicalTrendsData } = useChartData(filteredData);

  useEffect(() => {
    filterData();
  }, [leaderboardData, selectedRegion, selectedCountry, selectedAccountName, selectedProductLine, selectedIndustry, filterType]);

  useEffect(() => {
    identifyImprovementNeededItems();
  }, [filteredData]);

  const filterData = () => {
    let filtered = [...leaderboardData];
    
    if (selectedRegion !== "All") {
      filtered = filtered.filter(item => item.region === selectedRegion);
    }
    if (selectedCountry !== "All") {
      filtered = filtered.filter(item => item.country === selectedCountry);
    }
    if (selectedAccountName !== "All") {
      filtered = filtered.filter(item => item.account_name === selectedAccountName);
    }
    if (filterType === "product" && selectedProductLine !== "All") {
      filtered = filtered.filter(item => item.product_line === selectedProductLine);
    } else if (filterType === "industry" && selectedIndustry !== "All") {
      filtered = filtered.filter(item => item.industry === selectedIndustry);
    }
    
    setFilteredData(filtered);
  };

  const identifyImprovementNeededItems = () => {
    if (filteredData.length === 0) return;
    
    const overItems: ForecastAccuracyItem[] = [];
    const underItems: ForecastAccuracyItem[] = [];

    if (selectedRegion === "All" && selectedCountry === "All" && selectedAccountName !== "All" && 
        (filterType === "product" && selectedProductLine === "All" || filterType === "industry" && selectedIndustry === "All")) {
      const totalForecast = filteredData.reduce((sum, item) => sum + (item.forecast || 0), 0);
      const totalAchievement = filteredData.reduce((sum, item) => sum + (item.achievement || 0), 0);
      
      if (totalAchievement > 0) {
        const accuracy = (totalForecast / totalAchievement) * 100;
        if (accuracy > 100) {
          overItems.push({ name: selectedAccountName, accuracy });
        } else if (accuracy < 100) {
          underItems.push({ name: selectedAccountName, accuracy });
        }
      }
    } else {
      const groupedData = new Map<string, { forecast: number; actual: number }>();
      
      filteredData.forEach(item => {
        const forecast = item.forecast || 0;
        const actual = item.achievement || 0;
        if (actual === 0) return;

        let key = "";
        if (selectedRegion !== "All" && selectedCountry !== "All" && 
            (filterType === "product" && selectedProductLine !== "All" || filterType === "industry" && selectedIndustry !== "All")) {
          key = filterType === "product" ? item.product_line : item.industry;
        } else if (selectedRegion === "All") {
          key = item.region;
        } else if (filterType === "product" && selectedProductLine === "All") {
          key = item.product_line;
        } else if (filterType === "industry" && selectedIndustry === "All") {
          key = item.industry;
        } else if (filterType === "product") {
          key = item.region;
        } else {
          key = item.region;
        }

        if (key === "NA") return;

        if (!groupedData.has(key)) {
          groupedData.set(key, { forecast: 0, actual: 0 });
        }
        const group = groupedData.get(key)!;
        group.forecast += forecast;
        group.actual += actual;
      });

      groupedData.forEach((values, key) => {
        if (values.actual === 0) return;
        const accuracy = (values.forecast / values.actual) * 100;
        if (accuracy > 100) {
          overItems.push({ name: key, accuracy });
        } else if (accuracy < 100) {
          underItems.push({ name: key, accuracy });
        }
      });
    }

    overItems.sort((a, b) => b.accuracy - a.accuracy);
    underItems.sort((a, b) => a.accuracy - b.accuracy);
    
    setOverForecastedItems(overItems);
    setUnderForecastedItems(underItems);
  };

  const getFilterLabel = () => {
    const regionLabel = selectedRegion === "All" ? "" : selectedRegion;
    const countryLabel = selectedCountry === "All" ? "" : selectedCountry;
    const accountLabel = selectedAccountName === "All" ? "" : selectedAccountName;
    const typeLabel = filterType === "product" 
      ? selectedProductLine === "All" ? "" : selectedProductLine 
      : selectedIndustry === "All" ? "" : selectedIndustry;
    
    if (!regionLabel && !countryLabel && !accountLabel && !typeLabel) return "";
    
    const filterElements = [regionLabel, countryLabel, accountLabel, typeLabel].filter(Boolean).join(", ");
    return `(${filterElements})`;
  };

  const handleRegionChange = (value: string) => {
    setSelectedRegion(value);
    setSelectedCountry("All");
    setSelectedAccountName("All");
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    setSelectedAccountName("All");
  };

  const handleAccountNameChange = (value: string) => {
    setSelectedAccountName(value);
  };

  const handleProductLineChange = (value: string) => {
    setSelectedProductLine(value);
  };

  const handleIndustryChange = (value: string) => {
    setSelectedIndustry(value);
  };

  const handleFilterTypeChange = (value: "product" | "industry") => {
    setFilterType(value);
  };

  return (
    <div className={`text-white ${className || ''}`}>
      <FilterHeader
        selectedRegion={selectedRegion}
        selectedCountry={selectedCountry}
        selectedAccountName={selectedAccountName}
        countryOptions={countryOptions}
        accountOptions={accountOptions}
        selectedProductLine={selectedProductLine}
        selectedIndustry={selectedIndustry}
        filterType={filterType}
        onRegionChange={handleRegionChange}
        onCountryChange={handleCountryChange}
        onAccountNameChange={handleAccountNameChange}
        onProductLineChange={handleProductLineChange}
        onIndustryChange={handleIndustryChange}
        onFilterTypeChange={handleFilterTypeChange}
      />
      
      <div className="bg-secondary-light/5 p-2 rounded-lg border border-primary/20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <HistoricalTrendsChart data={historicalTrendsData} getFilterLabel={getFilterLabel} />
          <ForecastAccuracyChart data={chartData} getFilterLabel={getFilterLabel} />
        </div>
        
        <ImprovementsPanel
          overForecastedItems={overForecastedItems}
          underForecastedItems={underForecastedItems}
          getFilterLabel={getFilterLabel}
        />

        <DataTable
          isTableOpen={isTableOpen}
          setIsTableOpen={setIsTableOpen}
          filteredData={filteredData}
        />
      </div>
    </div>
  );
};

export default ForecastAccuracyTab;
